import type {
  ArenaGameplayDefinition,
  ArenaSide,
  BridgeDefinition,
  EntityId,
  EntityState,
  GroundRouteState,
  WorldPoint,
} from './types';
import {
  arenaSideOf,
  bridgeEntryPoint,
  bridgeExitPoint,
  distanceToRiver,
  footprintIntersectsRiver,
  hasClearedRiver,
  isAlignedWithBridge,
} from './navigation/geometry';

export const STUCK_REPLAN_TICKS = 30;
const ROUTE_COMMIT_DISTANCE = 500;
const ROUTE_SWITCH_ADVANTAGE = 750;
const FRIENDLY_CLEARANCE = 120;
const MAX_FRIENDLY_PENALTY_PER_ENTITY = 300;
const MAX_FRIENDLY_ROUTE_PENALTY = 600;

export type NavigationObstacle = Pick<EntityState, 'id' | 'owner' | 'kind' | 'movement' | 'position' | 'radius' | 'dead' | 'removed'>;

export type RouteTransition =
  | { type: 'selected'; bridgeId: string }
  | { type: 'committed'; bridgeId: string }
  | { type: 'crossing'; bridgeId: string }
  | { type: 'released'; bridgeId: string }
  | { type: 'replanned'; bridgeId: string; fromBridgeId: string; reason: 'target-lane-changed' | 'stuck-alternate-route' };

export interface NavigationPlan {
  waypoint: WorldPoint;
  route: GroundRouteState;
  obstaclePenalty: number;
  transition?: RouteTransition;
}

export const createGroundRouteState = (tick = 0): GroundRouteState => ({
  bridgeId: null,
  phase: 'free',
  sourceSide: null,
  targetSide: null,
  selectedForTargetId: null,
  selectedAtTick: tick,
  lastProgressTick: tick,
  lastWaypointDistance: null,
  noForwardProgressTicks: 0,
  replanCount: 0,
  stuckReplanUsed: false,
});

const distance = (left: WorldPoint, right: WorldPoint): number => Math.hypot(left.x - right.x, left.y - right.y);

const distanceToSegment = (point: WorldPoint, start: WorldPoint, end: WorldPoint): number => {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared <= 0) return distance(point, start);
  const projection = Math.max(0, Math.min(1, ((point.x - start.x) * dx + (point.y - start.y) * dy) / lengthSquared));
  return distance(point, { x: start.x + dx * projection, y: start.y + dy * projection });
};

const pathDistance = (points: readonly WorldPoint[]): number => {
  let total = 0;
  for (let index = 1; index < points.length; index += 1) total += distance(points[index - 1]!, points[index]!);
  return total;
};

const normalizedSide = (side: ArenaSide, fallback: Exclude<ArenaSide, 'river'>): Exclude<ArenaSide, 'river'> => side === 'river' ? fallback : side;

const routePoints = (
  entity: EntityState,
  target: WorldPoint,
  gameplay: ArenaGameplayDefinition,
  bridge: BridgeDefinition,
): WorldPoint[] => {
  const targetSide = arenaSideOf(gameplay, target);
  const fallbackSource = targetSide === 'bottom' ? 'top' : 'bottom';
  const sourceSide = entity.route.sourceSide ?? normalizedSide(arenaSideOf(gameplay, entity.position), fallbackSource);
  if (arenaSideOf(gameplay, entity.position) === 'river') {
    return [entity.position, bridgeExitPoint(gameplay, bridge, sourceSide, entity.radius), target];
  }
  return [
    entity.position,
    bridgeEntryPoint(gameplay, bridge, sourceSide, entity.radius),
    bridgeExitPoint(gameplay, bridge, sourceSide, entity.radius),
    target,
  ];
};

const friendlyCongestionPenalty = (
  entity: EntityState,
  points: readonly WorldPoint[],
  obstacles: readonly NavigationObstacle[],
  targetEntityId?: EntityId,
): number => {
  const segments = points.slice(1).map((end, index) => ({ start: points[index]!, end }));
  if (!segments.length) return 0;
  const total = obstacles
    .filter((obstacle) => obstacle.id !== entity.id
      && obstacle.id !== targetEntityId
      && obstacle.owner === entity.owner
      && obstacle.kind === 'troop'
      && obstacle.movement === 'ground'
      && !obstacle.dead
      && !obstacle.removed)
    .reduce((sum, obstacle) => {
      const nearest = Math.min(...segments.map((segment) => distanceToSegment(obstacle.position, segment.start, segment.end)));
      const overlap = Math.max(0, entity.radius + obstacle.radius + FRIENDLY_CLEARANCE - nearest);
      return sum + Math.min(MAX_FRIENDLY_PENALTY_PER_ENTITY, overlap * 0.75);
    }, 0);
  return Math.min(MAX_FRIENDLY_ROUTE_PENALTY, total);
};

const bridgeCost = (
  entity: EntityState,
  target: WorldPoint,
  gameplay: ArenaGameplayDefinition,
  bridge: BridgeDefinition,
  obstacles: readonly NavigationObstacle[],
  targetEntityId?: EntityId,
): { total: number; obstaclePenalty: number } => {
  const points = routePoints(entity, target, gameplay, bridge);
  const obstaclePenalty = friendlyCongestionPenalty(entity, points, obstacles, targetEntityId);
  return { total: pathDistance(points) + obstaclePenalty, obstaclePenalty };
};

const chooseBridge = (
  entity: EntityState,
  target: WorldPoint,
  gameplay: ArenaGameplayDefinition,
  obstacles: readonly NavigationObstacle[],
  targetEntityId?: EntityId,
  excludedBridgeId?: string,
): { bridge: BridgeDefinition; obstaclePenalty: number; cost: number } => {
  const candidates = gameplay.bridges
    .filter((bridge) => bridge.id !== excludedBridgeId || gameplay.bridges.length === 1)
    .map((bridge) => ({ bridge, ...bridgeCost(entity, target, gameplay, bridge, obstacles, targetEntityId) }))
    .sort((left, right) => left.total - right.total || left.bridge.id.localeCompare(right.bridge.id));
  const selected = candidates[0]!;
  return { bridge: selected.bridge, obstaclePenalty: selected.obstaclePenalty, cost: selected.total };
};

const routeSourceSide = (
  entity: EntityState,
  gameplay: ArenaGameplayDefinition,
  targetSide: ArenaSide,
): Exclude<ArenaSide, 'river'> => {
  if (entity.route.sourceSide) return entity.route.sourceSide;
  const current = arenaSideOf(gameplay, entity.position);
  return normalizedSide(current, targetSide === 'bottom' ? 'top' : 'bottom');
};

const routeWaypoint = (
  entity: EntityState,
  target: WorldPoint,
  gameplay: ArenaGameplayDefinition,
  bridge: BridgeDefinition,
  route: GroundRouteState,
): WorldPoint => {
  const sourceSide = route.sourceSide!;
  const currentSide = arenaSideOf(gameplay, entity.position);
  if (route.phase === 'crossing' || currentSide === 'river') return bridgeExitPoint(gameplay, bridge, sourceSide, entity.radius);
  if (currentSide !== sourceSide) return target;
  const entry = bridgeEntryPoint(gameplay, bridge, sourceSide, entity.radius);
  const aligned = isAlignedWithBridge(bridge, entity.position, entity.radius);
  const beforeEntry = sourceSide === 'bottom' ? entity.position.y > entry.y : entity.position.y < entry.y;
  return !aligned || beforeEntry ? entry : bridgeExitPoint(gameplay, bridge, sourceSide, entity.radius);
};

export function groundPathDistance(
  entity: EntityState,
  target: WorldPoint,
  gameplay: ArenaGameplayDefinition,
  obstacles: readonly NavigationObstacle[] = [],
  targetEntityId?: EntityId,
): number {
  if (entity.movement !== 'ground') return distance(entity.position, target);
  const sourceSide = arenaSideOf(gameplay, entity.position);
  const targetSide = arenaSideOf(gameplay, target);
  if (sourceSide === targetSide && sourceSide !== 'river') return distance(entity.position, target);
  return chooseBridge(entity, target, gameplay, obstacles, targetEntityId).cost;
}

export function routePlan(
  entity: EntityState,
  target: WorldPoint,
  gameplay: ArenaGameplayDefinition,
  obstacles: readonly NavigationObstacle[] = [],
  targetEntityId?: EntityId,
  tick = 0,
): NavigationPlan {
  if (entity.movement !== 'ground') return { waypoint: target, route: createGroundRouteState(tick), obstaclePenalty: 0 };

  const previous = entity.route;
  let route: GroundRouteState = { ...previous };
  let transition: RouteTransition | undefined;
  const targetSide = arenaSideOf(gameplay, target);

  if (route.bridgeId && hasClearedRiver(gameplay, entity.position, entity.radius, route.sourceSide)) {
    const releasedBridgeId = route.bridgeId;
    route = { ...createGroundRouteState(tick), replanCount: route.replanCount };
    transition = { type: 'released', bridgeId: releasedBridgeId };
  }

  const currentSide = arenaSideOf(gameplay, entity.position);
  const crossingRequired = targetSide === 'river' || currentSide === 'river' || currentSide !== targetSide;
  if (!crossingRequired && route.phase !== 'crossing') {
    return { waypoint: target, route, obstaclePenalty: 0, ...(transition ? { transition } : {}) };
  }

  const justSelected = !route.bridgeId;
  if (justSelected) {
    const selected = chooseBridge(entity, target, gameplay, obstacles, targetEntityId);
    const sourceSide = routeSourceSide(entity, gameplay, targetSide);
    route = {
      ...route,
      bridgeId: selected.bridge.id,
      phase: 'approach',
      sourceSide,
      targetSide: targetSide === 'river' ? (sourceSide === 'bottom' ? 'top' : 'bottom') : targetSide,
      selectedForTargetId: targetEntityId ?? null,
      selectedAtTick: tick,
      lastProgressTick: tick,
      lastWaypointDistance: null,
      noForwardProgressTicks: 0,
    };
    transition = { type: 'selected', bridgeId: selected.bridge.id };
  }

  const sourceSide = route.sourceSide!;
  const bridgeBeforeReplan = gameplay.bridges.find((candidate) => candidate.id === route.bridgeId)!;
  const isCrossing = footprintIntersectsRiver(gameplay, entity.position, entity.radius);
  const shouldCommit = isCrossing
    || route.phase === 'committed'
    || route.phase === 'crossing'
    || distanceToRiver(gameplay, entity.position, entity.radius, sourceSide) <= ROUTE_COMMIT_DISTANCE;
  const nextPhase = justSelected ? 'approach' : isCrossing ? 'crossing' : shouldCommit ? 'committed' : 'approach';
  if (nextPhase !== route.phase) {
    route = { ...route, phase: nextPhase };
    if (nextPhase === 'committed' && transition?.type !== 'selected') transition = { type: 'committed', bridgeId: route.bridgeId! };
    if (nextPhase === 'crossing') transition = { type: 'crossing', bridgeId: route.bridgeId! };
  }

  const targetChanged = route.selectedForTargetId !== (targetEntityId ?? null);
  const canChangeBridge = route.phase === 'approach';
  if (targetChanged && canChangeBridge) {
    const selected = chooseBridge(entity, target, gameplay, obstacles, targetEntityId);
    const current = bridgeCost(entity, target, gameplay, bridgeBeforeReplan, obstacles, targetEntityId);
    if (selected.bridge.id !== route.bridgeId && current.total - selected.cost >= ROUTE_SWITCH_ADVANTAGE) {
      const fromBridgeId = route.bridgeId!;
      route = {
        ...route,
        bridgeId: selected.bridge.id,
        selectedForTargetId: targetEntityId ?? null,
        selectedAtTick: tick,
        lastWaypointDistance: null,
        noForwardProgressTicks: 0,
        replanCount: route.replanCount + 1,
      };
      transition = { type: 'replanned', bridgeId: selected.bridge.id, fromBridgeId, reason: 'target-lane-changed' };
    } else {
      route = { ...route, selectedForTargetId: targetEntityId ?? null };
    }
  }

  if (route.noForwardProgressTicks >= STUCK_REPLAN_TICKS && route.phase === 'approach' && !route.stuckReplanUsed && gameplay.bridges.length > 1) {
    const fromBridgeId = route.bridgeId!;
    const selected = chooseBridge(entity, target, gameplay, obstacles, targetEntityId, fromBridgeId);
    route = {
      ...route,
      bridgeId: selected.bridge.id,
      selectedAtTick: tick,
      lastWaypointDistance: null,
      noForwardProgressTicks: 0,
      replanCount: route.replanCount + 1,
      stuckReplanUsed: true,
    };
    transition = { type: 'replanned', bridgeId: selected.bridge.id, fromBridgeId, reason: 'stuck-alternate-route' };
  }

  const bridge = gameplay.bridges.find((candidate) => candidate.id === route.bridgeId)!;
  const waypoint = routeWaypoint(entity, target, gameplay, bridge, route);
  const obstaclePenalty = friendlyCongestionPenalty(entity, [entity.position, waypoint], obstacles, targetEntityId);
  return { waypoint, route, obstaclePenalty, ...(transition ? { transition } : {}) };
}
